/***
*
*   USERS
*   Enables an admin to manage the users in their application
*
**********/

import Styles from './users.module.scss';
import React, { Fragment, useContext, useState, useEffect } from 'react';

// Module imports
import axios from 'axios';
import { Backdrop, TextField, MenuItem, Divider, ClickAwayListener, Button as MuiButton } from '@mui/material';
import { CSVLink } from "react-csv";
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';

// Components import.
import {
    ViewContext, Card, AccountNav, Table, Button, Animate,
    TitleRow, useAPI, Event, Message, AuthContext,
    Form, Icon, useTranslation,
    useCan
} from 'components/lib';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { Info } from '@mui/icons-material';

export function Users(props) {

    const context = useContext(ViewContext);
    const authContext = useContext(AuthContext);
    const data = useAPI('/api/account/users');
    const permission = useCan('account.users.view')

    const [deptData, setDeptData] = useState([]);
    const [users, setUsers] = useState([]);
    const [licenseUsage, setLicenseUsage] = useState({});
    const [paymentMode, setPaymentMode] = useState('');
    // const [isLms, setIsLms] = useState('')
    const [upgradeConfirm, setUpgradeConfirm] = useState(false);
    const [isExpiredUser, setIsExpiredUser] = useState(false);
    const [selectedBU, setSelectedBU] = useState('');
    const [selectedDept, setSelectedDept] = useState('N/A');
    const [selectedIsLead, setSelectedIsLead] = useState(0);
    const [selectedUsers, setSelectedUsers] = useState('');
    const [openAssignDepartment, setOpenAssignDepartment] = useState(false);
    const [openImportUsers, setOpenImportUsers] = useState(false);
    const [openLicenseUtilization, setOpenLicenseUtilization] = useState(false);
    const [roleOptions, setRoleOptions] = useState([]);
    const isLms = authContext?.user?.addons?.lms?.enabled || false;

    useEffect(() => {
        const newRoleOptions = Object.keys(licenseUsage.remainingLicenses || {}).map(role => ({
            label: role,
            value: role,
            disabled: (licenseUsage.remainingLicenses[role] || 0) === 0
        }));

        const hasAvailableOptions = newRoleOptions && newRoleOptions.length && newRoleOptions.some(option => !option.disabled);
        if (!hasAvailableOptions) {
            setRoleOptions([{ label: props.t('account.users.invite.text.noOptions'), value: "", disabled: true }]);
        } else {
            setRoleOptions(newRoleOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [licenseUsage]);

    function invite() {

        context.modal.show({
            title: props.t('account.users.invite.title'),
            text: props.t('account.users.invite.text.invitesLeft'),
            form: {
                email: {
                    label: props.t('account.users.invite.form.email.label'),
                    type: 'text',
                    required: true,
                },
                role: {
                    label: props.t('account.users.invite.form.permission.label'),
                    type: 'select',
                    options: roleOptions,
                    required: true
                },
            },
            buttonText: props.t('account.users.invite.form.button'),
            url: '/api/invite',
            method: 'POST',

        }, (form, res) => {

            // add the invited user to the
            if (res.length) {

                const state = [...users];
                // Count invites by permission to update license usage
                const invitesByRole = {};

                res.forEach(invite => {
                    if (!state.find(x => x.id === invite.id)) {

                        const role = invite.role;
                        // Count invites by permission
                        invitesByRole[role] = (invitesByRole[role] || 0) + 1;

                        state.push({

                            id: invite.id,
                            name: '',
                            email: invite.email,
                            date_created: invite.date_sent,
                            role: invite.role || 'orgscholar',
                            status: props.t('account.users.status.invited'),
                            bu_name: '',
                            dept_name: '',
                            actions: {

                                invite: resendInvite,
                                delete: deleteInvite

                            },
                        });
                    }
                });

                Event('invited_user');
                setUsers(state);
                setLicenseUsage(prev => ({
                    ...prev,
                    remainingLicenses: {
                        ...prev.remainingLicenses,
                        ...Object.keys(invitesByRole).reduce((acc, role) => {
                            acc[role] = Math.max(0, (prev.remainingLicenses[role] || 0) - invitesByRole[role]);
                            return acc;
                        }, {})
                    },
                    utilizedLicenses: {
                        ...prev.utilizedLicenses,
                        ...Object.keys(invitesByRole).reduce((acc, role) => {
                            acc[role] = (prev.utilizedLicenses[role] || 0) + invitesByRole[role];
                            return acc;
                        }, {})
                    }
                }));
            }
        });
    }

    // Buy more licenses
    async function createCheckout(data) {
        try {

            // Creating a checkout session to upgrade licenses.
            await axios({
                url: '/api/stripe/upgrade/licenses',
                method: 'post',
            }).then(function (response) {
                if (response.data?.checkout_url) {
                    window.location.href = response.data?.checkout_url;
                }
            });
        }
        catch (err) {
            context.handleError(err);
        }
    }

    // Renew Expired licenses
    function renewLicenses(data) {
        try {
            // Check if the data is an array.
            const multi = Array.isArray(data);
            // Ensure that all details are in array format.
            const userIds = multi ? data.map(item => item?.row?.id) : [data.id];
            const permission = multi ? data.map(item => item?.row?.permissions) : data.permissions;
            const status = multi ? data.map(item => item?.row?.status) : [data.status];

            // Check that the permission is 'orgscholar' and the status is 'Expired'.
            ((multi ? permission.every(item => item.includes('user.renew')) : permission.includes('user.renew')) && status.every(item => item === 'Expired')) ?
                (
                    // Creating a checkout session to renew licenses.
                    context.modal.show({
                        title: props.t('account.users.renew.form.title'),
                        form: {
                            userIds: {
                                type: 'hidden',
                                value: userIds,
                            }
                        },
                        buttonText: props.t('account.users.renew.form.button'),
                        text: props.t('account.users.renew.form.text'),
                        url: '/api/stripe/renew/licenses',
                        method: 'POST',
                    }, (form, response) => {
                        if (response) {
                            window.location.href = response;
                        }
                    })

                ) : (
                    context.notification.show(props.t('account.users.renew.notification'), 'warning', true)
                );
        }
        catch (err) {
            context.handleError(err);
        }
    }

    // eslint-disable-next-line no-unused-vars
    // function editUser(data, callback) {

    //     context.modal.show({
    //         title: props.t('account.users.edit.title'),
    //         form: {
    //             id: {
    //                 type: 'hidden',
    //                 value: data.id
    //             },
    //             name: {
    //                 label: props.t('account.users.edit.form.name.label'),
    //                 type: 'text',
    //                 required: true,
    //                 disabled: true,
    //                 value: data.name,
    //                 errorMessage: 'Please enter a name'
    //             },
    //             email: {
    //                 label: props.t('account.users.edit.form.email.label'),
    //                 type: 'email',
    //                 value: data.email,
    //                 required: true,
    //             },
    //             permission: {
    //                 label: props.t('account.users.edit.form.permission.label'),
    //                 type: data.permission === 'owner' ? null : 'select',
    //                 options: permissions?.data?.list?.filter(x => x.value !== 'owner'),
    //                 default: data.permission
    //             }
    //         },
    //         buttonText: props.t('account.users.edit.form.button'),
    //         url: '/api/user',
    //         method: 'PATCH'

    //     }, (res) => {
    //         context.notification.show(data.name + ' was updated', 'success', true);
    //         callback(res);

    //     });
    // }

    // function deleteUser(data, callback){

    //   context.modal.show({
    //     title: props.t('account.users.delete.title'),
    //     form: {},
    //     buttonText: props.t('account.users.delete.form.button'),
    //     text: `${props.t('account.users.delete.text')} ${data.name}?`,
    //     url: `/api/user/${data.id}`,
    //     method: 'DELETE',
    //     destructive: true

    //   }, () => {

    //     context.notification.show(data.name + ' was deleted', 'success', true);
    //     callback();

    //   });
    // }

    function assignDept(data) {
        const multi = Array.isArray(data);
        const userIds = multi ? data.map(item => item?.row?.id) : [data?.id];
        const permission = multi ? data.map(item => item?.row?.permissions) : data.permissions;
        setSelectedBU(data?.bu_id);
        setSelectedIsLead(data?.is_lead);
        setSelectedUsers(userIds);
        // Check that the permission is 'orgscholar' 
        if (multi ? permission.every(item => item.includes('bu.dept.assign')) : permission.includes('bu.dept.assign')) {
            if (data?.bu_id !== "N/A") {
                const options = {
                    method: "GET",
                    url: `/api/lms/bu/departments?bu_id=${data?.bu_id}`,
                    headers: {
                        "accept": "application/json",
                    }
                };
                axios
                    .request(options)
                    .then(function (response) {
                        setDeptData(response.data.data);
                        setSelectedDept(data?.dept_id);
                    })
                    .catch((err) => {
                        context.notification.show(`${err.response.data.error}`, 'error');
                    });
            }
            setOpenAssignDepartment(true);
        } else {
            context.notification.show(props.t('account.users.assignDept.notification'), 'warning', true)
        };
    }

    function deleteInvite(data, callback) {

        context.modal.show({
            title: props.t('account.users.delete_invite.title'),
            form: {},
            buttonText: props.t('account.users.delete_invite.form.button'),
            text: `${props.t('account.users.delete_invite.text')} ${data.email}?`,
            url: `/api/invite/${data.id}`,
            method: 'DELETE',
            destructive: true

        }, () => {

            setLicenseUsage(prev => ({
                ...prev,
                remainingLicenses: {
                    ...prev.remainingLicenses,
                    [data.role]: (prev.remainingLicenses[data.role] || 0) + 1
                },
                utilizedLicenses: {
                    ...prev.utilizedLicenses,
                    [data.role]: Math.max(0, (prev.utilizedLicenses[data.role] || 0) - 1)
                }
            }));
            context.notification.show(props.t('account.users.delete_invite.notification', { email: data.email }), 'success', true);
            callback();

        });
    }

    async function resendInvite(data) {
        try {

            await axios({

                url: '/api/invite',
                method: 'post',
                data: { email: data.email, role: data.role }

            });

            context.notification.show(`${props.t('account.users.invite.resent_notification')} ${data.email}`, 'success', true);

        }
        catch (err) {

            context.handleError(err);

        }
    }

    useEffect(() => {
        if (data) {
            setUsers(data?.data?.users || []);
            setLicenseUsage(data?.data?.licenses || {});
            setPaymentMode(data?.data?.plan || 'external');
            const status = data?.data?.users ? data?.data?.users.map(item => item?.status) : [];
            setIsExpiredUser(status.includes("Expired"));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // attach the per row actions for invites
    if (users.length) {
        users.forEach(u => {
            if (u.status === 'Invited') {
                u.actions = {

                    invite: resendInvite,
                    delete: deleteInvite

                }
            }
        })
    }


    const handleBUChange = (value) => {
        setSelectedBU(value);
        if (value !== "N/A") {
            const options = {
                method: "GET",
                url: `/api/lms/bu/departments?bu_id=${value}`,
                headers: {
                    "accept": "application/json",
                }
            };
            axios
                .request(options)
                .then(function (response) {
                    setDeptData(response.data.data);
                })
                .catch((err) => {
                    context.notification.show(`${err.response.data.error}`, 'error');
                });
        }
    };

    const handleAssignDept = () => {
        const formData = {
            bu_id: selectedBU,
            dept_id: selectedDept,
            userIds: selectedUsers,
            is_lead: selectedIsLead
        }
        const options = {
            method: "PATCH",
            url: `/api/user/lms/access`,
            headers: {
                "accept": "application/json",
            },
            data: formData
        };
        axios
            .request(options)
            .then(function (response) {
                setOpenAssignDepartment(false);
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            })
            .catch((err) => {
                context.notification.show(`${err.response.data.error}`, 'error');
                setOpenAssignDepartment(false);
            });
    };

    const ownerActions = {
        // disabled till licensing by numbers can be implemented
        // edit: editUser,
        // delete: deleteUser,
        email: true,
    }
    const message = {
        type: isExpiredUser ? "error" : "info",
        title: isExpiredUser ? props.t('account.users.message.title.expired') : props.t('account.users.message.title.manage'),
        closable: !isExpiredUser,
        text: isExpiredUser ?
            `${props.t('account.users.message.text.expired.text')} ${paymentMode === "external" ?
                props.t('account.users.message.text.expired.external') :
                props.t('account.users.message.text.expired.default')}`
            : props.t('account.users.message.text.default')
    }

    let showData = ['email', 'name', 'date_created', 'last_login', 'role', 'status'];
    if (isLms) {
        showData.push('bu_name', 'dept_name');
        ownerActions.custom = [
            { icon: 'briefcase', action: assignDept }
        ]

    }

    const importCsv = () => {
        setOpenImportUsers(true)
    }

    return (
        <Fragment>
            {data?.loading ?
                <LoadingBackdrop isOpen={true} /> :
                <>
                    <ConfirmationCard
                        title={props.t('account.users.upgradeLicense.confirm.title')}
                        body={props.t('account.users.upgradeLicense.confirm.body')}
                        showCard={upgradeConfirm}
                        setShowCard={setUpgradeConfirm}
                        handleConfirm={createCheckout}
                    />
                    <AccountNav />
                    {permission && 
                        <Message
                            {...message}

                        />
                    }
                    <Animate>

                        <TitleRow title={props.t('account.users.subtitle')}>
                            <div style={{ display: "flex", gap: "0.7em" }}>
                                {(paymentMode === 'payment') && <Button small text={props.t('account.users.upgradeLicense.button')} action={() => { setUpgradeConfirm(true) }} />}
                                <Button small text={props.t('account.users.new.button')} action={invite} />
                                <Button small text={props.t('account.users.new.importCSV')} action={importCsv} />
                            </div>
                        </TitleRow>

                        <Card className={Styles.card}>
                            {permission && 
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1em' }}>
                                    <MuiButton variant='text' onClick={() => { setOpenLicenseUtilization(true) }}>
                                        <Info sx={{ mr: 1 }} />{props.t('account.users.info')}
                                    </MuiButton>
                                </div>
                            }
                            <Table
                                search
                                {...((paymentMode === 'payment' || isLms) && { selectable: true })}
                                className='restrict-width'
                                data={users}
                                loading={data.loading}
                                show={showData}
                                badge={{
                                    col: 'status', color: 'blue', condition: [

                                        { value: props.t('account.users.status.verified'), color: 'green' },
                                        { value: props.t('account.users.status.active'), color: 'green' },
                                        { value: props.t('account.users.status.registered'), color: 'blue' },
                                        { value: props.t('account.users.status.invited'), color: 'orange' },
                                        { value: props.t('account.users.status.expired'), color: 'red' }

                                    ]
                                }}
                                actions={ownerActions}
                                bulkActions={{
                                    custom: [
                                        ...(paymentMode === 'payment'
                                            ? [{ text: props.t('account.users.tableActions.renew'), color: "orange", action: renewLicenses }]
                                            : []),
                                        ...(isLms
                                            ? [{ text: props.t('account.users.tableActions.assignDept'), color: "green", action: assignDept }]
                                            : []),
                                    ]
                                }}
                                translation='account.users.table'
                            />
                        </Card>

                        {openAssignDepartment &&
                            <Backdrop
                                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                                open={openAssignDepartment}
                                invisible={!openAssignDepartment}
                            >
                                <div id='assignDeptContainer' className={Styles.assignDeptContainer}>
                                    <p
                                        id='deptTitle'
                                        className={Styles.deptTitle}
                                    >
                                        {props.t('account.users.assignDept.title')} </p>
                                    <TextField
                                        id={`business-unit`}
                                        name="bu"
                                        select
                                        required
                                        fullWidth
                                        label={props.t('account.users.assignDept.textField.selectedBU.label')}
                                        value={selectedBU || "N/A"}
                                        SelectProps={{
                                            MenuProps: { disableScrollLock: true },
                                            defaultValue: "N/A"
                                        }}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            handleBUChange(value)
                                        }}
                                    >
                                        <MenuItem key={"blankValue"} value={props.t('account.users.assignDept.textField.selectedBU.defaultOption')}>
                                            N/A
                                        </MenuItem>
                                        {data?.data?.businessUnits?.map(bu => (
                                            <MenuItem key={bu.id} value={bu.id}>
                                                {bu.bu_name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        id={`department-unit`}
                                        name="dept"
                                        select
                                        required
                                        fullWidth
                                        label={props.t('account.users.assignDept.textField.department.label')}
                                        value={(selectedBU && selectedDept) ? selectedDept : "N/A"}
                                        SelectProps={{
                                            MenuProps: { disableScrollLock: true },
                                            defaultValue: "N/A"
                                        }}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setSelectedDept(value);
                                        }}
                                        helperText={selectedBU === "N/A" ? props.t('account.users.assignDept.textField.department.helperText') : " "}
                                    >
                                        <MenuItem key={"blankValue"} value={"N/A"}>
                                            {props.t('account.users.assignDept.textField.department.option')}
                                        </MenuItem>
                                        {deptData?.map(dept => (
                                            <MenuItem key={dept.id} value={dept.id}>
                                                {dept.dept_name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    {!(selectedUsers?.length > 1) && <TextField TextField
                                        id={`is-lead`}
                                        name="lead"
                                        select
                                        required
                                        fullWidth
                                        label={props.t('account.users.assignDept.textField.lead.label')}
                                        value={selectedIsLead ?? 0}
                                        SelectProps={{
                                            MenuProps: { disableScrollLock: true },
                                            defaultValue: "N/A"
                                        }}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setSelectedIsLead(value);
                                        }}
                                    >
                                        <MenuItem key={0} value={0}>
                                            {props.t('account.users.assignDept.textField.lead..option.false')}
                                        </MenuItem>
                                        <MenuItem key={1} value={1}>
                                            {props.t('account.users.assignDept.textField.lead.option.true')}
                                        </MenuItem>
                                    </TextField>}
                                    <div id='btnContainer' className={Styles.btnContainer}>
                                        <AivagamButton
                                            id='cancelButton'
                                            buttonText={props.t('account.users.assignDept.button.cancel')}
                                            onClick={() => { setOpenAssignDepartment(false) }}
                                            type="cancel"
                                        />
                                        <AivagamButton
                                            id='confirmButton'
                                            buttonText={props.t('account.users.assignDept.button.assignDept')}
                                            onClick={() => { handleAssignDept() }}
                                            type="confirm"
                                            disabled={selectedDept === "N/A"}
                                        />
                                    </div>
                                </div>
                            </Backdrop>
                        }
                        {openImportUsers &&
                            <ImportUsersComponent
                                openImportUsers={openImportUsers}
                                setOpenImportUsers={setOpenImportUsers}
                                context={context}
                                licensesAvailable={licenseUsage?.remainingLicenses}
                                existingUsers={users}
                                permissionOptions={roleOptions}
                            />
                        }
                        {openLicenseUtilization &&
                            <LicenseUtilizationComponent
                                openLicenseUtilization={openLicenseUtilization}
                                setOpenLicenseUtilization={setOpenLicenseUtilization}
                                licenses={licenseUsage}
                            />
                        }
                    </Animate>
                </>
            }
        </Fragment >
    );
}

function ImportUsersComponent(props) {
    const { t } = useTranslation();
    const [uploadDone, setUploadDone] = useState(false);
    const [customError, setCustomError] = useState({
        error: false,
        type: '',
        title: '',
        message: '',
        data: []
    })
    return (
        <Backdrop
            sx={{
                color: '#fff',
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)'
            }}
            open={props.openImportUsers}
            invisible={!props.openImportUsers}
        >
            <ClickAwayListener onClickAway={() => { props.setOpenImportUsers(false) }}>
                <div className={Styles.importModalContainer}>
                    {!customError.error ? (
                        <>
                            {/* Modal Header */}
                            <div className={Styles.modalHeader}>
                                <button
                                    className={Styles.closeBtn}
                                    onClick={() => props.setOpenImportUsers(false)}
                                >
                                    <FeatherIcon icon='x' size={20} />
                                </button>
                                <h1 className={Styles.modalTitle}>
                                    {t('account.users.invite.importUsers.title')}
                                </h1>
                                <p className={Styles.modalSubtitle}>
                                    {t('account.users.invite.importUsers.subtitle')}
                                </p>
                            </div>

                            {/* Modal Content */}
                            <div className={Styles.modalContent}>
                                {/* Step 1: Download Template */}
                                <div className={Styles.downloadSection}>
                                    <div className={Styles.sectionHeader}>
                                        <h2 className={Styles.sectionTitle}>
                                            <span className={Styles.stepNumber}>1</span>
                                            {t('account.users.invite.importUsers.sampleCSV.title')}
                                        </h2>
                                        <p className={Styles.sectionDescription}>
                                            {t('account.users.invite.importUsers.sampleCSV.content')}
                                        </p>
                                    </div>

                                    <div className={Styles.sectionContent}>
                                        <CSVLink
                                            data={[{ id: 1, emailId: 'johndoe@getnada.com' }]}
                                            headers={[{ label: 'rowId', key: 'id' }, { label: 'emailId', key: 'emailId' }]}
                                            filename="users.csv"
                                            className={Styles.downloadLink}
                                        >
                                            <button className={Styles.downloadButton}>
                                                <FeatherIcon icon="download" size={16} />
                                                {t('account.users.invite.importUsers.sampleCSV.button')}
                                            </button>
                                        </CSVLink>
                                    </div>
                                </div>

                                {/* Divider */}
                                <div className={Styles.dividerContainer}>
                                    <Divider
                                        orientation="vertical"
                                        variant="middle"
                                        flexItem
                                        sx={{
                                            backgroundColor: "#e5e7eb",
                                            width: '1px'
                                        }}
                                    />
                                </div>

                                {/* Step 2: Upload File */}
                                <div className={Styles.uploadSection}>
                                    <div className={Styles.sectionHeader}>
                                        <h2 className={Styles.sectionTitle}>
                                            <span className={Styles.stepNumber}>2</span>
                                            {t('account.users.invite.importUsers.uploadCSV.title')}
                                        </h2>
                                        <p className={Styles.sectionDescription}>
                                            {t('account.users.invite.importUsers.uploadCSV.content')}
                                        </p>
                                    </div>

                                    <div className={Styles.sectionContent}>
                                        <div className={Styles.fileSizeLimit}>
                                            <FeatherIcon icon="alert-circle" size={16} />
                                            Maximum file size: 10MB
                                        </div>

                                        <div className={Styles.uploadFormContainer}>
                                            {!uploadDone ? (
                                                <Form
                                                    buttonText={t('account.users.invite.importUsers.uploadCSV.button')}
                                                    url={`/api/user/upload/csv`}
                                                    method={"POST"}
                                                    inputs={{
                                                        users_csv: {
                                                            type: 'file',
                                                            max: 1,
                                                            required: true,
                                                            maxFileSize: 10000,
                                                            accept: [],
                                                        },
                                                        permission: {
                                                            label: `${t('account.users.invite.form.permission.label')}`,
                                                            type: 'select',
                                                            options: props.permissionOptions,
                                                            required: true
                                                        },
                                                        licenses_available: {
                                                            type: 'hidden',
                                                            value: JSON.stringify(props.licensesAvailable),
                                                        }
                                                    }}
                                                    callback={(res) => {
                                                        setUploadDone(true);
                                                        props.setOpenImportUsers(false);
                                                        window.location.reload();
                                                        props.context.notification.show(res.message, 'success', true);
                                                    }}
                                                    error={(err) => {
                                                        setUploadDone(true);
                                                        if (err.response.status === 500) {
                                                            props.context.notification.show(err.response.data.message, 'error', true);
                                                            props.setOpenImportUsers(false);
                                                        } else {
                                                            setCustomError({
                                                                error: true,
                                                                type: err.response.data?.errorType,
                                                                title: err.response.data?.errorData?.title,
                                                                message: err.response.data?.errorData?.message,
                                                                data: err.response.data?.errorData?.data
                                                            });
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <div className={Styles.successMessageContainer}>
                                                    <Message
                                                        title={t('account.users.invite.importUsers.uploadCSV.message.title')}
                                                        type='success'
                                                        text={t('account.users.invite.importUsers.uploadCSV.message.text')}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className={Styles.errorContainer}>
                            <CustomError
                                closable={true}
                                title={customError.title}
                                type={customError.type}
                                text={customError.message}
                                items={customError.data}
                                onClose={() => { props.setOpenImportUsers(false) }}
                            />
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </Backdrop >
    )
}

function CustomError(props) {
    const type = props.type;

    const icon = {
        exceeds_licenses: 'alert-circle',
        invalid_emails: 'alert-triangle',
        duplicate_emails: 'users',
        existing_emails: 'user-x',
    };

    const color = {
        exceeds_licenses: 'red',
        invalid_emails: 'red',
        duplicate_emails: 'red',
        existing_emails: 'red'
    };

    // Use styles from CSS modules
    const messageStyle = `${Styles.message} ${Styles[`${color[type]}Bg`]} ${props.className || ''}`;
    const titleStyle = `${Styles.title} ${Styles[`${color[type]}Text`]}`;

    return (
        <div className={messageStyle}>
            <Icon
                className={Styles.icon}
                size={30}
                color={color[type]}
                image={icon[type]}
            />

            {props.closable &&
                <Button
                    icon='x'
                    position='absolute'
                    className={Styles.close}
                    action={props.onClose}
                />
            }

            <section>
                {props.title &&
                    <h1 className={titleStyle}>
                        {props.title}
                    </h1>}

                {props.text &&
                    <p>{props.text}</p>}

                {props.children && props.children}

                {props.items && props.items.length > 0 &&
                    <table className={Styles.table}>
                        <thead>
                            <tr>
                                <th>rowId</th>
                                <th>emailId</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.items.map((item, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                }
            </section>
        </div>
    );
}

function LicenseUtilizationComponent(props) {
    const { t } = useTranslation();

    // Get roles from totalLicenses (since all objects have same permissions)
    const roles = props.licenses.totalLicenses ? Object.keys(props.licenses.totalLicenses) : [];

    return (
        <Backdrop
            sx={{
                color: '#fff',
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)'
            }}
            open={props.openLicenseUtilization}
            invisible={!props.openLicenseUtilization}
        >
            <ClickAwayListener onClickAway={() => props.setOpenLicenseUtilization(false)}>
                <div className={Styles.licenseUtilizationContainer}>
                    <div className={Styles.licenseUtilizationHeader}>
                        <h2 className={Styles.licenseUtilizationTitle}>
                            {t('account.users.licenseUtilization.title')}
                        </h2>
                        <FeatherIcon
                            onClick={() => props.setOpenLicenseUtilization(false)}
                            icon='x'
                            style={{ cursor: 'pointer' }}
                        />
                    </div>

                    <div className={Styles.licenseUtilizationContent}>
                        <table className={Styles.licenseUtilizationTable}>
                            <thead>
                                <tr>
                                    <th>{t('account.users.licenseUtilization.table.role')}</th>
                                    <th>{t('account.users.licenseUtilization.table.total')}</th>
                                    <th>{t('account.users.licenseUtilization.table.utilized')}</th>
                                    <th>{t('account.users.licenseUtilization.table.remaining')}</th>
                                    <th>{t('account.users.licenseUtilization.table.utilization')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roles.map((role) => {
                                    const total = props.licenses.totalLicenses[role];
                                    const utilized = props.licenses.utilizedLicenses[role];
                                    const remaining = props.licenses.remainingLicenses[role];
                                    const utilizationPercent = total > 0 ? Math.round((utilized / total) * 100) : 0;

                                    return (
                                        <tr key={role}>
                                            <td className={Styles.permissionCell}>
                                                <span className={Styles.permissionBadge}>
                                                    {role}
                                                </span>
                                            </td>
                                            <td className={Styles.numberCell}>{total}</td>
                                            <td className={Styles.numberCell}>{utilized}</td>
                                            <td style={{ color: !remaining ? 'red' : '' }} className={Styles.numberCell}>{remaining}</td>
                                            <td style={{ color: utilizationPercent >= 85 ? 'red' : '' }} className={Styles.percentCell}>{utilizationPercent}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {roles.length === 0 && (
                            <div className={Styles.noDataMessage}>
                                {t('account.users.licenseUtilization.noData')}
                            </div>
                        )}
                    </div>

                    <div className={Styles.licenseUtilizationFooter}>
                        <AivagamButton
                            buttonText={t('account.users.licenseUtilization.button.close')}
                            onClick={() => props.setOpenLicenseUtilization(false)}
                            type="cancel"
                        />
                    </div>
                </div>
            </ClickAwayListener>
        </Backdrop>
    );
}
